import {getTestProps} from '@benchling/js-test-utils';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useCallback} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {AgentType, ChatType} from 'graphql-client/__generated/graphqlGlobalTypes';
import PlusMenu from 'llm/chat-library/PlusMenu';
import {
  getAgentTypeText,
  getAgentDescriptionForChatInput,
  getAvailableAgentTypes,
  doesAssistantDeprecateAgent,
  doesAgentTypeSupportRealtimeVoice,
  getDefaultAgentType,
  isAgentTypeInAssistantUnifiedDropdown,
} from 'llm/chat-library/sidepanel/ChatTypeUtil';
import AIButton from 'llm/chat-library/ui/AIButton';
import AIMenu from 'llm/chat-library/ui/AIMenu';
import {AI_GRAY_175, AI_GRAY_475} from 'llm/chat-library/ui/AIStyleConstants';
import {
  RealtimeVoiceSessionControls,
  RealtimeVoiceSessionState,
} from 'llm/realtime-voice/useRealtimeVoiceSession';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import Menu from 'ui-library/menus/Menu';
import {MenuHeight, MenuSelectedIconPlacement, MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import Section from 'ui-library/menus/Section';
import Spinner from 'ui-library/Spinner';
import isLikelyMobileCapabilities from 'util/isLikelyMobileCapabilities';

const OTHER_MODELS_MENU_VALUE = '__other_models__';

export function UnifiedAssistantAgentToolbarDropdown({
  disabled,
  selectedAgentType,
  unifiedAgentsInOrder,
  setAgentType,
}: {
  disabled: boolean;
  selectedAgentType: AgentType;
  unifiedAgentsInOrder: Array<AgentType>;
  setAgentType: (agentType: AgentType | null) => void;
}): JSX.Element {
  const hasAssistant = unifiedAgentsInOrder.includes(AgentType.ASSISTANT);
  const hasCoScientist = unifiedAgentsInOrder.includes(AgentType.CO_SCIENTIST);
  const legacyAgents = unifiedAgentsInOrder.filter((agentType) => doesAssistantDeprecateAgent(agentType));
  const isActive = true;

  const menu = (
    <AIMenu
      // TODO: Avoid focusing first menu item if menu was opened by mouse.
      scrollable={false}
      height={MenuHeight.SMALL}
      width={MenuWidth.XLARGE}
      value={selectedAgentType}
      useSelectedIconIfSingleSelect={true}
      selectedIconPlacement={MenuSelectedIconPlacement.TRAILING}
    >
      <Section>
        {hasAssistant && (
          <Option
            value={AgentType.ASSISTANT}
            onSelect={() => {
              setAgentType(AgentType.ASSISTANT);
            }}
            metadata={
              getAgentDescriptionForChatInput(AgentType.ASSISTANT) != null ? (
                <span className={css(styles.agentMenuOptionSubtitle)}>
                  {getAgentDescriptionForChatInput(AgentType.ASSISTANT)}
                </span>
              ) : null
            }
          >
            {getAgentTypeText(AgentType.ASSISTANT)}
          </Option>
        )}
        {hasCoScientist && (
          <Option
            value={AgentType.CO_SCIENTIST}
            onSelect={() => {
              setAgentType(AgentType.CO_SCIENTIST);
            }}
            metadata={
              getAgentDescriptionForChatInput(AgentType.CO_SCIENTIST) != null ? (
                <span className={css(styles.agentMenuOptionSubtitle)}>
                  {getAgentDescriptionForChatInput(AgentType.CO_SCIENTIST)}
                </span>
              ) : null
            }
          >
            {getAgentTypeText(AgentType.CO_SCIENTIST)}
          </Option>
        )}
        {legacyAgents.length > 0 && (
          <Option className={css(styles.agentMenuOtherModelsOption)} value={OTHER_MODELS_MENU_VALUE}>
            Legacy agents
            <Menu
              width={MenuWidth.MEDIUM}
              dropUp={true}
              selectedIconPlacement={MenuSelectedIconPlacement.TRAILING}
            >
              {legacyAgents.map((agentOption) => (
                <Option
                  key={agentOption}
                  value={agentOption}
                  onSelect={() => {
                    setAgentType(agentOption);
                  }}
                  metadata={
                    getAgentDescriptionForChatInput(agentOption) != null ? (
                      <span className={css(styles.agentMenuOptionSubtitle)}>
                        {getAgentDescriptionForChatInput(agentOption)}
                      </span>
                    ) : null
                  }
                >
                  {getAgentTypeText(agentOption)}
                </Option>
              ))}
            </Menu>
          </Option>
        )}
      </Section>
    </AIMenu>
  );

  return (
    <Dropdown
      menu={menu}
      closeOnInsideClick={true}
      closeOnOutsideClick={true}
      allowMenuToCloseDropdown={false}
    >
      <Button
        aria-pressed={isActive}
        className={classNames({'is-active': isActive}, css(styles.agentTypeButton))}
        size={ButtonSize.MINI}
        buttonStyle={isActive ? ButtonStyle.TERTIARY_BLUE : ButtonStyle.TERTIARY_WHITE}
        textClassName={css(styles.agentTypeText)}
        text={getAgentTypeText(selectedAgentType)}
        disabled={disabled}
        isDropdown={true}
        dataTestAttrs={{component: 'ChatInput', element: 'agentToggle', key: selectedAgentType}}
      />
    </Dropdown>
  );
}

export function AgentToolbar({
  chatSessionId,
  textareaRef,
  fileInputRef,
  cameraInputRef,
  allowedChatTypes,
  agentType,
  setAgentType,
  inputDisabled,
  publicDataEnabled,
  setPublicDataEnabled,
  selectedMcpServerIds,
  setSelectedMcpServerIds,
  realtimeVoiceSessionControls,
}: {
  chatSessionId: string | null;
  textareaRef: React.RefObject<HTMLTextAreaElement>;
  allowedChatTypes: Array<ChatType>;
  agentType: AgentType | null;
  setAgentType: (agentType: AgentType | null) => void;
  publicDataEnabled: boolean;
  setPublicDataEnabled: (publicDataEnabled: boolean) => void;
  selectedMcpServerIds?: Array<string> | undefined;
  setSelectedMcpServerIds?: (serverIds: Array<string> | undefined) => void;
  fileInputRef: React.RefObject<HTMLInputElement>;
  cameraInputRef: React.RefObject<HTMLInputElement>;
  inputDisabled: boolean;
  realtimeVoiceSessionControls: RealtimeVoiceSessionControls;
}): JSX.Element {
  const handleCameraButtonClick = useCallback((): void => {
    if (!inputDisabled && cameraInputRef.current) {
      cameraInputRef.current.click();
    }
  }, [inputDisabled, cameraInputRef]);

  return (
    <div
      {...getTestProps({component: 'AgentToolbar', element: 'root'})}
      className={css(styles.agentTypeButtons)}
      onMouseDown={(e) => {
        // Keep toolbar interactions from moving focus inside the chat input group.
        // This ensures :focus-within styles activate only when the textarea itself is focused.
        e.preventDefault();
      }}
    >
      <PlusMenu
        fileInputRef={fileInputRef}
        agentType={agentType}
        inputDisabled={inputDisabled}
        publicDataEnabled={publicDataEnabled}
        setPublicDataEnabled={setPublicDataEnabled}
        selectedMcpServerIds={selectedMcpServerIds}
        setSelectedMcpServerIds={setSelectedMcpServerIds}
      />
      {/* Show a spinner while settings are loading (only possible for existing chats) */}
      {agentType == null ? (
        <Spinner aria-label="Loading agent settings" />
      ) : (
        (() => {
          const availableAgentTypes = getAvailableAgentTypes(allowedChatTypes);
          const defaultAgentType = getDefaultAgentType(null, allowedChatTypes);
          const isNonDefaultAgent = agentType !== defaultAgentType;
          const unifiedAgentsInOrder = availableAgentTypes.filter((a) =>
            isAgentTypeInAssistantUnifiedDropdown(a)
          );
          const showUnifiedAssistantDropdown =
            BenchlingConfig.LLM_ENABLE_ASSISTANT_AGENT &&
            unifiedAgentsInOrder.length >= 2 &&
            (!isNonDefaultAgent || isAgentTypeInAssistantUnifiedDropdown(agentType));

          let unifiedAssistantDropdownRendered = false;

          return availableAgentTypes.map((option: AgentType) => {
            if (showUnifiedAssistantDropdown && unifiedAgentsInOrder.includes(option)) {
              if (unifiedAssistantDropdownRendered) {
                return null;
              }
              unifiedAssistantDropdownRendered = true;
              return (
                <UnifiedAssistantAgentToolbarDropdown
                  key="unified-assistant-agents"
                  disabled={inputDisabled}
                  selectedAgentType={agentType}
                  unifiedAgentsInOrder={unifiedAgentsInOrder}
                  setAgentType={setAgentType}
                />
              );
            }

            // with an X to return to the default. When a unified-dropdown agent is selected, other
            // agents stay visible and the unified
            // control has no close — switch via the dropdown or another agent's toggle.
            if (
              isNonDefaultAgent &&
              option !== agentType &&
              !(showUnifiedAssistantDropdown && isAgentTypeInAssistantUnifiedDropdown(agentType))
            ) {
              return null;
            }

            const showCloseIcon = isNonDefaultAgent && availableAgentTypes.length > 1 && option === agentType;
            const toggleButton = (
              <ToggleButton
                key={option}
                agentType={option}
                isActive={option === agentType}
                showCloseIcon={showCloseIcon}
                onClick={() => {
                  if (option === agentType) {
                    if (!showCloseIcon) {
                      return;
                    }
                    setAgentType(defaultAgentType);
                    return;
                  }
                  setAgentType(option);
                }}
                text={getAgentTypeText(option)}
                disabled={inputDisabled}
                testKey={option}
              />
            );
            return toggleButton;
          });
        })()
      )}
      {isLikelyMobileCapabilities() && (
        <AIButton
          ariaLabel="Take and upload photo"
          disabled={inputDisabled}
          icon="snapshot"
          onClick={handleCameraButtonClick}
        />
      )}
      {agentType != null &&
        doesAgentTypeSupportRealtimeVoice(agentType) &&
        chatSessionId != null &&
        (() => {
          switch (realtimeVoiceSessionControls.sessionState) {
            case RealtimeVoiceSessionState.NOT_CONNECTED:
              return (
                <AIButton
                  onClick={realtimeVoiceSessionControls.startVoiceSession}
                  disabled={inputDisabled}
                  icon="microphone"
                  text="Go live"
                />
              );
            case RealtimeVoiceSessionState.CONNECTING:
              return <AIButton disabled={true} text="Connecting..." />;
            case RealtimeVoiceSessionState.CONNECTED:
              return (
                <AIButton
                  onClick={realtimeVoiceSessionControls.stopVoiceSession}
                  disabled={inputDisabled}
                  icon="stop"
                  text="Stop"
                />
              );
            default:
              return null;
          }
        })()}
    </div>
  );
}

/**
 * A toggle button used to pick an agent or an agent feature like public data.
 */
function ToggleButton(props: {
  agentType: AgentType;
  isActive: boolean;
  text: React.ReactNode;
  showCloseIcon: boolean;
  onClick: () => void;
  disabled: boolean;
  testKey?: string;
}): JSX.Element {
  const {agentType, isActive, text, showCloseIcon, onClick, disabled, testKey} = props;
  const tooltipText = getAgentDescriptionForChatInput(agentType);
  const button = (
    // NB: I did not see an out of the box toggle button in Mantine v6.
    <Button
      aria-pressed={isActive}
      className={classNames({'is-active': isActive}, css(styles.agentTypeButton))}
      size={ButtonSize.MINI}
      buttonStyle={isActive ? ButtonStyle.TERTIARY_BLUE : ButtonStyle.TERTIARY_WHITE}
      onClick={onClick}
      textClassName={css(styles.agentTypeText)}
      text={
        <>
          {text}
          {showCloseIcon && <BenchlingIcon className={css(styles.closeIcon)} icon="close" />}
        </>
      }
      disabled={disabled}
      dataTestAttrs={testKey ? {component: 'ChatInput', element: 'agentToggle', key: testKey} : undefined}
    />
  );
  if (tooltipText == null) {
    return button;
  }
  return (
    <Tooltip text={tooltipText} placement={FloatingPlacement.TOP} delay={{open: 0, close: 0}}>
      {button}
    </Tooltip>
  );
}

const styles = StyleSheet.create({
  agentTypeButtons: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '6px',
    fontSize: '12px',
  },
  agentTypeButton: {
    fontWeight: 400,
    fontSize: '13px',
    padding: '4px 10px 5px',
  },
  closeIcon: {
    marginLeft: '6px',
    marginRight: 0, // button has padding.
  },
  agentTypeText: {
    flex: 'none',
  },
  agentMenuOptionSubtitle: {
    color: AI_GRAY_475,
    fontSize: '12px',
  },
  agentMenuOtherModelsOption: {
    borderTop: `1px solid ${AI_GRAY_175}`,
    marginTop: 6,
    paddingTop: 6,
  },
});
